import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { ReactNode, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetLosIdsForCompanyDocument, LoanStatus } from '@willow/graphql-iso/src/app';
import { CompanyId, LosId } from '@willow/types-iso';

import { Hint } from '../Hint';
import { WillowMultiSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  fieldName: FieldPathByValue<T, string[]>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  excludeStatuses?: LoanStatus[];
  hint?: ReactNode;
};

interface LosSelectOption {
  label: LosId;
  value: LosId;
}

export function ControlledLosIdMultiSelect<T extends FieldValues>({
  companyId,
  fieldName,
  control,
  hideErrorMessage = false,
  label,
  placeholder,
  required,
  excludeStatuses,
  hint,
}: Props<T>) {
  const {
    formState: { errors },
    fieldState: { error },
  } = useController({ control, name: fieldName });

  const { data } = useQuery(GetLosIdsForCompanyDocument, { variables: { companyId, excludeStatuses } });
  const losOptions: LosSelectOption[] = useMemo(() => {
    const loans = data?.company.loanIdentifiers ?? [];
    return loans.map((loan) => ({
      value: loan.losId,
      label: loan.losId,
    }));
  }, [data?.company.loanIdentifiers]);

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1">*</span>}
        </Form.Label>
      )}

      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowMultiSelect
            placeholder={placeholder}
            options={losOptions}
            value={value ? losOptions.filter((option) => value.includes(option.value)) : []}
            isInvalid={!!error}
            closeMenuOnSelect={false}
            onChange={(selected) => onChange(selected ? selected.map((option) => option.value) : [])}
          />
        )}
      />

      {hint && <Hint>{hint}</Hint>}

      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="mt-1 u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
